import { CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

// Get confidence styling
export function getConfidenceStyle(level, score) {
  if (level === 'high' || score >= 0.8) {
    return { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', icon: CheckCircle };
  } else if (level === 'medium' || score >= 0.6) {
    return { color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', icon: AlertTriangle };
  } else {
    return { color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30', icon: XCircle };
  }
}

// Format currency
export function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value || 0);
}

// Format YYYY-MM-DD date string to user-friendly format (e.g., "Jan 15, 2024")
export function formatDate(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) return dateStr;
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Get next business day as YYYY-MM-DD string (skips weekends)
// Earliest valid ship date the CSR can request (also the seed default and the date
// input's min). Same-day cutoff (Tonya, 2026-07-20): before 2pm local on a weekday,
// today is allowed (supports the unofficial same-day-shipment cutoff / on-time reports);
// at/after 2pm, or on a weekend, it rolls to the next business day.
export function getNextBusinessDay() {
  const now = new Date();
  // Work in local date parts (not toISOString/UTC, which can shift the day in the
  // evening for US timezones and would break the "today" case).
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isWeekday = date.getDay() >= 1 && date.getDay() <= 5;
  const beforeCutoff = now.getHours() < 14; // 2pm local
  if (!(isWeekday && beforeCutoff)) date.setDate(date.getDate() + 1);
  if (date.getDay() === 6) date.setDate(date.getDate() + 2); // Sat → Mon
  if (date.getDay() === 0) date.setDate(date.getDate() + 1); // Sun → Mon
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Format unit price with at least 4 decimal places
export function formatUnitPrice(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 5
  }).format(value || 0);
}

// Characters stripped from any CSR free-text (notes, comments) before it enters an order.
// NOT about XML validity — the cXML builder (ElementTree) escapes & < > correctly. This is
// defense-in-depth against Delta field-mapping breakage AND SQL-injection-style input.
// Pass maxLen to also cap the length — A+ field widths vary, so the caller supplies the limit.
//
// Set confirmed with Trey (2026-07-08): keep the special chars blocked (incl. comma and &,
// which CSRs asked for) and ALSO block ( ) ; and the -- SQL-comment sequence — the classic
// "ITEMA'); DROP TABLE Orders;--" tokens. Mirror of the backend _ERP_FORBIDDEN_CHARS in
// po-processor-app-function/services/erp_submission_service.py — keep the two in sync.
const ERP_FORBIDDEN_CHARS = /[#"'&%|!=,<>();]/g;
// Runs of 2+ hyphens (the SQL line-comment "--"); single hyphens are left alone so item
// numbers like 10-45140 still work in free-text.
const ERP_FORBIDDEN_SEQ = /-{2,}/g;

export function sanitizeErpText(value, maxLen = Infinity) {
  return String(value ?? '')
    .replace(ERP_FORBIDDEN_CHARS, '')
    .replace(ERP_FORBIDDEN_SEQ, '')
    .slice(0, maxLen);
}

// A PDF often arrives with a wrong/generic MIME type (e.g. Crystal Reports exports as
// application/octet-stream), so treat a .pdf extension as authoritative too.
// Days from today (local) to a YYYY-MM-DD date; null when it isn't one.
function daysFromToday(iso, today = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return null;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((date - base) / 86400000);
}

// A date the AI read with a likely wrong year (Rose Acre 1553234, 2026-10-12: handwritten
// "10/9/26" came back as 2025-10-09). Margins are wide on purpose: a PO processed a day or
// a week after it was written never trips these. Returns a short warning or null.
export function dateYearWarning(iso, kind, today = new Date()) {
  const days = daysFromToday(iso, today);
  if (days === null) return null;
  if (kind === 'po') {
    if (days > 7) return 'PO date is in the future — check the year on the PO';
    if (days < -183) return 'PO date is over 6 months old — check the year on the PO';
  } else if (kind === 'ship' && days > 366) {
    return 'More than a year out — check the year on the PO';
  }
  return null;
}

export function isPdfFile(name, type) {
  return type === 'application/pdf' || String(name || '').toLowerCase().endsWith('.pdf');
}

// Files shown as plain text in the preview: .txt, plus the XML/JSON order files the backend
// reads (cXML from Ariba, 2026-10-26). A .cxml file has no browser MIME type, so go by
// extension too.
export function isTextFile(name, type) {
  return ['text/plain', 'text/xml', 'application/xml', 'application/json'].includes(type)
    || /\.(txt|xml|cxml|json)$/i.test(String(name || ''));
}

// EDI renders (e.g. Liaison EDINotepad, Southwire 2026-10-06) arrive as .html. Match on the
// extension too, since a saved attachment can come through with a generic MIME type.
export function isHtmlFile(name, type) {
  return type === 'text/html' || /\.html?$/i.test(String(name || ''));
}

// The preview frame blocks the file's scripts, so EDI renders whose sections expand by script
// (Liaison EDINotepad: "+ Additional Information" -> <div class='hidden'> holding the line
// description and dates) would stay collapsed. Appending this stylesheet shows those sections
// and hides the dead +/- buttons. It only targets div.hidden, so other hidden elements (e.g.
// Liaison's span.debug trace text) stay hidden.
const EXPAND_COLLAPSED_SECTIONS =
  '<style>div.hidden{display:block !important;visibility:visible !important}' +
  'a.expander{display:none !important}</style>';

export function withExpandedSections(html) {
  return html ? `${html}${EXPAND_COLLAPSED_SECTIONS}` : html;
}

// ITEMBAL setup is TRI-STATE: true (set up) / false (genuinely not set up) / null (couldn't
// determine). The backend sends `in_itembal: null` when the ITBAL lookup itself failed — pool
// down, query timeout, or no warehouse resolved (a non-numeric ship-to like 'HA' made
// sp_GetItemInventory throw SQL 245, emptying inventory for a whole customer). `null` must NOT
// collapse to `false`: false renders as red "NOT SET UP IN ITEMBAL" and hard-blocks submit, so a
// swallowed SQL error would tell the CSR a definitive lie about master data and cost them the
// order. Letting an unknown line through is safe — the submit-time guard
// (erp_submission_service._check_itembal) re-queries ITBAL and still rejects for real.
//
// Lives here rather than in App.jsx because LineItems/ItemSearchModal need it too and importing
// from App.jsx would be circular.
//   raw === undefined => field absent (older backend / ?mock=1 fixture); keep the legacy
//   `!!inventory` behavior rather than newly permitting everything.
export function deriveInItembal(raw, inventory) {
  if (raw === true || raw === false) return raw;
  if (inventory) return true;              // stock on hand proves an ITBAL row exists
  return raw === null ? null : false;      // null = unknown; undefined = legacy absent
}

// --- GL / OE cost + M% (Tonya Conner, 2026-09-24) ---
// A+ fills GL Cost from the item's warehouse average cost (ITBAL.IBAVCS) and OE Cost as
// GL + load: 5% on stock orders (A+ applies it; CSRs don't change it) and 2% on drop-ship
// (the CSR sets it — confirmed by Tonya). Costs carry 5 decimals and A+ TRUNCATES rather
// than rounds (order AI1PD: 0.74476 × 1.05 = 0.781998 stored as 0.78199).
export const STOCK_OE_LOAD = 1.05;
export const DROPSHIP_OE_LOAD = 1.02;

export function truncateCost(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  // Round away float noise at 9 places first so 0.288 × 1.02 lands on 0.29376, not 0.29375.
  return Math.floor(Math.round(n * 1e9) / 1e4) / 1e5;
}

// Cost input: digits + one '.', ≤7 whole digits and ≤5 decimals (A+ shows e.g. 3.88620).
export function sanitizeCostInput(raw) {
  const cleaned = String(raw || '').replace(/[^0-9.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  const w = whole.slice(0, 7);
  return rest.length ? `${w}.${rest.join('').slice(0, 5)}` : w;
}

// Finished cost entry → fixed 5 decimals, or '' when blank/invalid.
export function normalizeCostInput(raw) {
  const s = sanitizeCostInput(raw);
  if (!s || s === '.') return '';
  const n = Number(s);
  return Number.isFinite(n) ? n.toFixed(5) : '';
}

// The OE Cost that goes with a GL Cost at a load (STOCK_OE_LOAD / DROPSHIP_OE_LOAD), truncated like A+.
export function oeFromGl(glCost, load) {
  const s = sanitizeCostInput(glCost);
  if (!s || s === '.') return '';
  const oe = truncateCost(Number(s) * load);
  return oe == null ? '' : oe.toFixed(5);
}

// A line's GL/OE cost as shown in the review UI (strings, 5 decimals, '' when unknown).
// Stock: A+'s own values — read-only. Drop-ship: A+'s GL + the 2% load as the default, with
// the CSR's typed value winning (`item.glCost` / `item.oeCost`; undefined = not overridden, so
// an un-typed OE keeps following GL).
export function getLineCosts(item, isDropShip) {
  const aplusGl = item.aplusGlCost != null && Number.isFinite(Number(item.aplusGlCost))
    ? Number(item.aplusGlCost).toFixed(5)
    : '';
  if (!isDropShip) {
    return { glCost: aplusGl, oeCost: oeFromGl(aplusGl, STOCK_OE_LOAD), editable: false, aplusGl };
  }
  const glCost = item.glCost !== undefined ? item.glCost : aplusGl;
  const oeCost = item.oeCost !== undefined ? item.oeCost : oeFromGl(glCost, DROPSHIP_OE_LOAD);
  return {
    glCost,
    oeCost,
    editable: true,
    aplusGl,
    overridden: item.glCost !== undefined || item.oeCost !== undefined,
  };
}

// M% as A+ shows it. PENDING CONFIRMATION: our A+ reference defines margin as
// (price − OE) / price; notes from Tonya's demo read "OE / final price". Keep the formula in
// this one place so it can be switched once Tonya confirms against a real A+ line
// (e.g. AI1PD line 10: 6.9% vs 93.1%).
export function marginPct(sellPrice, oeCost) {
  const price = Number(sellPrice);
  const oe = Number(oeCost);
  if (!(price > 0) || oeCost === '' || oeCost == null || !Number.isFinite(oe)) return null;
  return ((price - oe) / price) * 100;
}

// recheckInventory's `costs` map → an item's cost fields. A null/absent map (lookup failed, or
// an older backend) leaves the line's cost alone; an item missing from a real map has no ITBAL
// row at that warehouse, so its cost is cleared.
export function costFieldsFromRecheck(costs, itemNumber) {
  if (!costs) return {};
  const cost = costs[itemNumber];
  return { aplusGlCost: cost?.gl_cost ?? null, costPricingUom: cost?.pricing_uom || null,
           costPricingPerStock: cost?.pricing_per_stock ?? null };
}
